// P3 (clinic-live-card-visits): the card's visit panel with live Raintree states.
//
// The backend (`GET /crm/patients/{id}/live-visits`) merges the stored rows with
// the patient's Raintree visits. Between checks the card keeps acting on the
// stored list (book, cancel, reschedule refresh `/appointments` only), so this
// overlays the CURRENT stored list on the last live answer - no new Raintree
// call after an action:
//   * a row the portal cancelled since the check disappears, unless Raintree
//     refused the cancel: then it stays, as a Raintree visit with no buttons;
//   * a row the portal moved since the check shows at its new time, Upcoming;
//   * a row the portal booked since the check is added, Upcoming.
// Returns null when the live answer isn't usable; the panel then shows the
// stored list exactly as it does today.

export function minuteOf(value) {
  if (!value) return "";
  return String(value).trim().replace(/(?:[Zz]|[+-]\d{2}:?\d{2})$/, "").slice(0, 16);
}

// W3 item 9: the clinic-day rule for rows the browser builds (a visit booked or
// moved since the check, and the stored rows while checking). These rows carry no
// clinic timezone, so the day is the house wall-clock rule's Pacific day
// (lib/wallClock.js); the server's own answer uses the clinic's zone (tz_for).
// No imports here: the node harness runs this file on its own.
export function pacificToday(now = new Date()) {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit",
    day: "2-digit" }).formatToParts(now).reduce((acc, x) => ({ ...acc, [x.type]: x.value }), {});
  return `${p.year}-${p.month}-${p.day}`;
}

// true when the visit's wall-clock DAY is before today's (a past day, not just a past time)
export function isPastDay(value, now = new Date()) {
  const day = minuteOf(value).slice(0, 10);
  return !!day && day < pacificToday(now);
}

const CURRENT = new Set(["upcoming", "not_checked_in"]);
const BROWSER_LABELS = { upcoming: "Upcoming", not_checked_in: "Not checked in yet" };

// "upcoming" (still ahead), "not_checked_in" (started, same day) or null (a past day)
function browserState(value, past, pastDay) {
  if (!past(value)) return "upcoming";
  return pastDay(value) ? null : "not_checked_in";
}

export function liveRowsFor(live, stored, refusedIds, isPast, isPastDayFn) {
  if (!live || !live.enabled || live.status !== "ok" || !Array.isArray(live.rows)) return null;
  const past = typeof isPast === "function" ? isPast : () => false;
  const pastDay = typeof isPastDayFn === "function" ? isPastDayFn : (v) => isPastDay(v);
  const refused = refusedIds instanceof Set ? refusedIds : new Set(refusedIds || []);
  const byId = new Map((stored || []).map((a) => [a.id, a]));
  const seen = new Set();
  const out = [];
  for (const r of live.rows) {
    if (r.appointment_id == null) {
      out.push(r);
      continue;
    }
    const s = byId.get(r.appointment_id);
    if (!s) {
      if (refused.has(r.appointment_id) && r.raintree_appointment_id) {
        out.push({
          ...r,
          key: `r${r.raintree_appointment_id}`,
          id: null,
          appointment_id: null,
          source: "raintree",
          actions: false,
        });
      }
      continue;
    }
    seen.add(s.id);
    if (minuteOf(s.appointment_datetime) !== minuteOf(r.stored_datetime)) {
      out.push(freshRow(s, past, pastDay));
    } else {
      // #51's cancel prompt reads these from the stored row (Caleb's review of #54)
      out.push({ ...r, patient_texted: s.patient_texted, patient_confirmed_at: s.patient_confirmed_at,
        cancel_text_on: s.cancel_text_on });
    }
  }
  for (const s of stored || []) {
    if (!seen.has(s.id)) out.push(freshRow(s, past, pastDay));
  }
  out.sort((a, b) => (minuteOf(b.appointment_datetime) > minuteOf(a.appointment_datetime) ? 1
    : minuteOf(b.appointment_datetime) < minuteOf(a.appointment_datetime) ? -1 : 0));
  return out;
}

function freshRow(s, past, pastDay) {
  const st = browserState(s.appointment_datetime, past, pastDay);
  return {
    ...s,
    key: `s${s.id}`,
    appointment_id: s.id,
    stored_datetime: s.appointment_datetime,
    source: "portal",
    state: st,
    state_label: st ? BROWSER_LABELS[st] : null,
    actions: st != null,
  };
}

export function liveStatusLine(live, checking) {
  if (!live || !live.enabled) return null;
  if (checking) return "Checking Raintree…";
  if (live.status === "ok" && live.checked_at) {
    const d = new Date(live.checked_at);
    if (!Number.isNaN(d.getTime())) {
      return `Checked with Raintree ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
    }
  }
  return "Couldn’t check Raintree";
}

// P3, add A + the approved wording (Matt, Oct 24). Exactly ONE message for a
// cancel Raintree didn't take, never #51's success or patient-text line:
//   refused (today's 200 + `raintree_error`, or a 409): Raintree said no;
//   failed (a 502, any other non-2xx, or no answer): Raintree may have
//   cancelled it after all, so the wording doesn't say it didn't.
// Raintree's reason, when there is one, is a second, muted line. A clean
// cancel keeps #51's toast unchanged (the caller shows it).
export function refusalReason(data) {
  if (data && typeof data.raintree_error === "string" && data.raintree_error.trim()) return data.raintree_error.trim();
  const d = data ? data.detail : undefined;
  if (d && typeof d === "object" && typeof d.message === "string" && d.message.trim()) return d.message.trim();
  if (typeof d === "string" && d.trim()) return d.trim();
  return null;
}

// W3 (clinic-cancel-keep-on-refusal, Matt's wording Oct 28): the routes now keep the visit
// and answer the contract's 409 {code: raintree_refused | raintree_not_found[, reason]} or
// 502 {code: raintree_unavailable, message: the fixed text}. Keyed by code, reason and
// whether the visit is already past. The refused mode expires waiting reminder texts, so the
// wording never says "nothing changed": the visit stays on the card.
export const UNAVAILABLE_TEXT =
  "The visit may still be booked in Raintree. Check the card's Raintree visits before trying again.";

export function cancelOutcome(status, data, dateText, isPast) {
  const ok = typeof status === "number" && status >= 200 && status < 300;
  const hasError = !!(data && data.raintree_error);
  if (ok && !hasError) return { refused: false, kind: "ok", dropStored: true, message: null, description: null };
  const visit = dateText ? `the ${dateText} visit` : "the visit";
  const d = data && data.detail && typeof data.detail === "object" ? data.detail : {};
  if (status === 502 && d.code === "raintree_unavailable") {
    const fixed = typeof d.message === "string" && d.message.trim() ? d.message.trim() : UNAVAILABLE_TEXT;
    return { refused: true, kind: "failed", dropStored: false, message: fixed, description: null };
  }
  const refused = (ok && hasError) || status === 409;
  if (!refused) {
    return {
      refused: true, kind: "failed", dropStored: false,
      message: `Couldn’t reach Raintree to cancel ${visit}. It may still be on the schedule. Check again or cancel it in Raintree.`,
      description: refusalReason(data),
    };
  }
  let message;
  if (d.reason === "not_cancellable") {
    message = `Raintree has ${visit} as checked in, completed or a no-show, so it can’t be cancelled. It stays on the card.`;
  } else if (isPast) {
    message = `Raintree didn’t cancel ${visit}, so it stays on the card. If the patient didn’t come, mark it a no-show in Raintree.`;
  } else {
    message = `Raintree didn’t cancel ${visit}, so it’s still booked and stays on the card. Try again, or cancel it in Raintree.`;
  }
  return { refused: true, kind: "refused", dropStored: ok, message, description: refusalReason(data) };
}

// W3 item 5 (Matt's wording, Oct 28): a reschedule whose old visit Raintree didn't cancel
// (the PUT's appointment.orphaned_raintree_appointment_id).
export function rescheduleOrphanText(oldDay) {
  const old = oldDay ? `the old ${oldDay} visit` : "the old visit";
  return `The new time is booked, but Raintree didn’t cancel ${old}. Cancel it in Raintree so the patient isn’t booked twice.`;
}

// Layout helpers (Matt's pick, Oct 24): next visit up top, the rest as a list.
// W3 item 9: a visit not checked in yet (same day) is current, like an upcoming one.
export const isUpcoming = (r) => CURRENT.has(r.state) || (r.state == null && r.upcoming === true);

const soonestFirst = (a, b) => (minuteOf(a.appointment_datetime) < minuteOf(b.appointment_datetime) ? -1 : 1);
const newestFirst = (a, b) => (minuteOf(a.appointment_datetime) < minuteOf(b.appointment_datetime) ? 1 : -1);

// W3 item 13: the groups. `next` is the soonest CURRENT visit (upcoming or not
// checked in yet); `upcoming` is the other current visits plus any other visit
// still ahead by date (Not in Raintree, Held by clinic...: never `next`), soonest
// first; `cancelled` holds every cancelled visit, future ones included, newest
// first; `past` is the rest, newest first. A row with no state (the stored list
// while checking) goes by its own `upcoming` flag.
export function splitVisits(rows, isPast) {
  const past = typeof isPast === "function" ? isPast : (v) => minuteOf(v) < nowWallMinute();
  const all = rows || [];
  const current = all.filter(isUpcoming).sort(soonestFirst);
  const next = current[0] || null;
  const cancelled = all.filter((r) => r.state === "cancelled").sort(newestFirst);
  const rest = all.filter((r) => !isUpcoming(r) && r.state !== "cancelled");
  const aheadOther = rest.filter((r) => r.state != null && !past(r.appointment_datetime));
  const upcoming = current.slice(1).concat(aheadOther).sort(soonestFirst);
  const pastRows = rest.filter((r) => !aheadOther.includes(r)).sort(newestFirst);
  return { next, upcoming, past: pastRows, cancelled };
}

// "now" as a Pacific wall-clock minute string (the house rule), for splitVisits' default
function nowWallMinute(now = new Date()) {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(now)
    .reduce((acc, x) => ({ ...acc, [x.type]: x.value }), {});
  return `${p.year}-${p.month}-${p.day}T${p.hour === "24" ? "00" : p.hour}:${p.minute}`;
}

// W3 item 13: the year only outside the current (Pacific) year
export function showYear(value, now = new Date()) {
  const y = minuteOf(value).slice(0, 4);
  return !!y && y !== pacificToday(now).slice(0, 4);
}

// W3 item 13: WestStar's own clinics drop the "West Star -" / "WestStar" prefix on
// the one-line rows; network (partner) names stay as they are.
export function shortClinic(name, partner) {
  const n = String(name || "").trim();
  if (!n || partner) return n || null;
  const s = n.replace(/^west\s?star\s*(?:[-–—]\s*)?/i, "").trim();
  return s || n;
}

const CHIP = {
  upcoming: "bg-blue-100 text-blue-700",
  not_checked_in: "bg-amber-100 text-amber-700",
  attended: "bg-emerald-100 text-emerald-700",
  no_show: "bg-amber-100 text-amber-700",
  cancelled: "bg-slate-100 text-slate-500",
  not_in_raintree: "bg-red-100 text-red-700",
};

export function chipClass(state) {
  return CHIP[state] || "bg-purple-100 text-purple-700";
}

export function sameClinic(a, b) {
  if (!a || !b) return false;
  return (a.name || "") === (b.name || "");
}

// While checking, or when Raintree couldn't be checked: the stored visits in the
// same layout, with no states (no chip). Upcoming ones keep today's buttons.
// W3 item 9 (Matt, Oct 31): a same-day visit past its start shows "Not checked in
// yet" with its buttons here too, until the end of the day.
export function fallbackRows(stored, isPast, isPastDayFn) {
  const past = typeof isPast === "function" ? isPast : () => false;
  const pastDay = typeof isPastDayFn === "function" ? isPastDayFn : (v) => isPastDay(v);
  return (stored || []).map((a) => {
    const st = browserState(a.appointment_datetime, past, pastDay);
    if (st === "not_checked_in") {
      return { ...a, key: `s${a.id}`, appointment_id: a.id, state: st, state_label: BROWSER_LABELS[st],
        upcoming: true, actions: true };
    }
    const up = st === "upcoming";
    return { ...a, key: `s${a.id}`, appointment_id: a.id, state: null, state_label: null, upcoming: up, actions: up };
  });
}

export const PAST_SHOWN = 3; // W3 item 13: three past visits, then "show n more"

// Browser side of the per-patient cache (5 minutes, like the server): a card
// remounted for the SAME patient (P18 remounts the card on every queue Next)
// reuses the answer instead of asking again. Refresh and the re-read after a
// cancel skip it; a cancel drops it.
export const LIVE_CACHE_MS = 5 * 60 * 1000;
const _liveCache = new Map(); // patient id -> { at, data }

export function liveCacheGet(patientId, now = Date.now()) {
  const hit = _liveCache.get(patientId);
  if (!hit || now - hit.at >= LIVE_CACHE_MS) return null;
  return hit.data;
}

export function liveCacheSet(patientId, data, now = Date.now()) {
  if (patientId == null || !data) return;
  if (_liveCache.size > 200) _liveCache.clear();
  _liveCache.set(patientId, { at: now, data });
}

export function liveCacheDrop(patientId) {
  _liveCache.delete(patientId);
}
